import React from 'react';
import { GameState, PieceType, Color } from '../../engine/types';
import './CapturedPieces.css';

interface CapturedPiecesProps {
  gameState: GameState;
}

// Starting position piece counts (excluding kings - they're never captured)
const STARTING_PIECES: Record<PieceType, number> = {
  [PieceType.Pawn]: 8,
  [PieceType.Knight]: 2,
  [PieceType.Bishop]: 2,
  [PieceType.Rook]: 2,
  [PieceType.Queen]: 1,
  [PieceType.King]: 1, // Include for type safety, but never counted as captured
};

/**
 * Calculate which pieces have been captured for a given color
 * Returns an array of piece types that have been captured
 */
function calculateCapturedPieces(gameState: GameState, capturedColor: Color): PieceType[] {
  // Count current pieces on board for the captured color
  const currentCounts: Record<PieceType, number> = {
    [PieceType.Pawn]: 0,
    [PieceType.Knight]: 0,
    [PieceType.Bishop]: 0,
    [PieceType.Rook]: 0,
    [PieceType.Queen]: 0,
    [PieceType.King]: 0,
  };

  // Count pieces currently on the board
  for (let i = 0; i < 64; i++) {
    const piece = gameState.board[i];
    if (piece && piece.color === capturedColor) {
      currentCounts[piece.type]++;
    }
  }

  // Calculate captured pieces (starting count - current count)
  const captured: PieceType[] = [];
  
  for (const pieceType of [PieceType.Queen, PieceType.Rook, PieceType.Bishop, PieceType.Knight, PieceType.Pawn]) {
    const startingCount = STARTING_PIECES[pieceType];
    const currentCount = currentCounts[pieceType];
    const capturedCount = startingCount - currentCount;
    
    // Add each captured piece to the array
    for (let i = 0; i < capturedCount; i++) {
      captured.push(pieceType);
    }
  }

  return captured;
}

/**
 * Get Unicode symbol for a piece type and color
 */
function getPieceSymbol(pieceType: PieceType, color: Color): string {
  const whiteSymbols = {
    [PieceType.King]: '♔',
    [PieceType.Queen]: '♕',
    [PieceType.Rook]: '♖',
    [PieceType.Bishop]: '♗',
    [PieceType.Knight]: '♘',
    [PieceType.Pawn]: '♙',
  };

  const blackSymbols = {
    [PieceType.King]: '♚',
    [PieceType.Queen]: '♛',
    [PieceType.Rook]: '♜',
    [PieceType.Bishop]: '♝',
    [PieceType.Knight]: '♞',
    [PieceType.Pawn]: '♟',
  };

  return color === Color.White ? whiteSymbols[pieceType] : blackSymbols[pieceType];
}

export const CapturedPieces: React.FC<CapturedPiecesProps> = ({ gameState }) => {
  const whiteCaptured = calculateCapturedPieces(gameState, Color.White);
  const blackCaptured = calculateCapturedPieces(gameState, Color.Black);

  return (
    <div className="captured-pieces">
      <h3 className="captured-pieces-title">Captured Pieces</h3>
      
      <div className="captured-section">
        <div className="captured-header">
          <span className="captured-label">White Captured</span>
          <span className="captured-count">{whiteCaptured.length}</span>
        </div>
        <div className="captured-display">
          {whiteCaptured.length === 0 ? (
            <span className="captured-empty">None</span>
          ) : (
            whiteCaptured.map((pieceType, index) => (
              <span key={index} className="captured-piece captured-piece-white">
                {getPieceSymbol(pieceType, Color.White)}
              </span>
            ))
          )}
        </div>
      </div>

      <div className="captured-section">
        <div className="captured-header">
          <span className="captured-label">Black Captured</span>
          <span className="captured-count">{blackCaptured.length}</span>
        </div>
        <div className="captured-display">
          {blackCaptured.length === 0 ? (
            <span className="captured-empty">None</span>
          ) : (
            blackCaptured.map((pieceType, index) => (
              <span key={index} className="captured-piece captured-piece-black">
                {getPieceSymbol(pieceType, Color.Black)}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
